import React, { useMemo, useState } from 'react';
import { Search, Check, X } from 'lucide-react';

/**
 * Pill button per un singolo store. Estratto a livello modulo per evitare
 * la regola eslint `react/no-unstable-nested-components` e per consentire
 * a React di mantenere stato / animazioni stabili tra render.
 */
function StorePill({ store, selected, disabled, onToggle, testIdPrefix }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onToggle(store.store_id)}
      className={`text-xs px-2 py-1 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        selected
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
      }`}
      data-testid={`${testIdPrefix}-${store.store_id}`}
      title={store.city || ''}
    >
      {selected && <Check className="w-3 h-3 mr-1 inline-block" />}
      {store.display_name || store.name}
    </button>
  );
}

/**
 * StoreMultiSelector
 * ------------------
 * Componente unificato per assegnare uno o più negozi a un'entità
 * (utente, dipendente, ruolo). Usato in:
 *  - UsersPage: edit inline + InviteDialog
 *  - StaffPage: CreateStaffDialog "Negozi aggiuntivi"
 *  - StaffMemberPage: edit "Negozi aggiuntivi (rotazione)"
 *
 * Funzionalità:
 *  - Ricerca testuale (attiva sopra ~6 store)
 *  - Pulsanti "Tutti" / "Nessuno" (configurabili)
 *  - Contatore X/Y selezionati
 *  - Esclusione opzionale di uno store (es. lo store primario non può
 *    essere anche tra i "secondari").
 *  - Raggruppamento opzionale per brand (utile sopra ~10 store).
 */
export default function StoreMultiSelector({
  stores = [],
  value = [],
  onChange,
  excludeStoreId = null,
  disabled = false,
  testIdPrefix = 'store-toggle',
  maxHeight = 'max-h-60',
  showCount = true,
  showControls = true,
  groupByBrand = false,
  brands = [],
  emptyText = 'Nessun negozio disponibile.',
  helperText = '',
}) {
  const [query, setQuery] = useState('');

  // Lista filtrata: rimuove lo store escluso e applica ricerca testuale.
  const filteredStores = useMemo(() => {
    let list = stores;
    if (excludeStoreId) list = list.filter(s => s.store_id !== excludeStoreId);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(s =>
        (s.display_name || s.name || '').toLowerCase().includes(q) ||
        (s.city || '').toLowerCase().includes(q) ||
        (s.brand_id || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [stores, excludeStoreId, query]);

  // Tutti gli ID selezionabili (post-esclusione, pre-filtro): serve per
  // "Seleziona tutti" che NON deve dipendere dalla query corrente.
  const selectableIds = useMemo(
    () => stores.filter(s => s.store_id !== excludeStoreId).map(s => s.store_id),
    [stores, excludeStoreId]
  );

  const allSelected = selectableIds.length > 0 && selectableIds.every(id => value.includes(id));
  const noneSelected = value.length === 0;
  const searchable = stores.length > 6;
  const showGroups = groupByBrand && brands.length > 1 && stores.length > 10;

  const toggle = (sid) => {
    if (disabled) return;
    const next = value.includes(sid)
      ? value.filter(x => x !== sid)
      : [...value, sid];
    onChange(next);
  };

  const selectAll = () => {
    if (disabled) return;
    onChange(selectableIds);
  };

  const selectNone = () => {
    if (disabled) return;
    onChange([]);
  };

  if (stores.length === 0) {
    return <p className="text-xs text-slate-400 p-2">{emptyText}</p>;
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-container`}>
      {showControls && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          {showCount && (
            <span className="text-[11px] text-slate-500 font-medium" data-testid={`${testIdPrefix}-count`}>
              {value.length}/{selectableIds.length} selezionati
            </span>
          )}
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={disabled || allSelected}
              onClick={selectAll}
              className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`${testIdPrefix}-select-all`}
            >
              Tutti
            </button>
            <button
              type="button"
              disabled={disabled || noneSelected}
              onClick={selectNone}
              className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`${testIdPrefix}-select-none`}
            >
              Nessuno
            </button>
          </div>
        </div>
      )}

      {searchable && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca negozio…"
            disabled={disabled}
            className="w-full text-xs pl-7 pr-7 py-1.5 border border-slate-200 focus:outline-none focus:border-slate-400 bg-white disabled:opacity-50"
            data-testid={`${testIdPrefix}-search`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
              aria-label="Cancella ricerca"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      <div className={`border border-slate-200 p-2 ${maxHeight} overflow-y-auto`}>
        {filteredStores.length === 0 ? (
          <p className="text-xs text-slate-400 p-2">Nessun negozio corrisponde a &quot;{query}&quot;</p>
        ) : showGroups ? (
          <div className="space-y-3">
            {brands.map(brand => {
              const brandStores = filteredStores.filter(s => s.brand_id === brand.brand_id);
              if (brandStores.length === 0) return null;
              return (
                <div key={brand.brand_id}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
                    {brand.display_name || brand.name || brand.brand_id}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {brandStores.map(s => (
                      <StorePill key={s.store_id} store={s} selected={value.includes(s.store_id)} disabled={disabled} onToggle={toggle} testIdPrefix={testIdPrefix} />
                    ))}
                  </div>
                </div>
              );
            })}
            {/* Fallback per store senza brand */}
            {(() => {
              const orphan = filteredStores.filter(s => !brands.some(b => b.brand_id === s.brand_id));
              if (orphan.length === 0) return null;
              return (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Altri</div>
                  <div className="flex flex-wrap gap-1">
                    {orphan.map(s => (
                      <StorePill key={s.store_id} store={s} selected={value.includes(s.store_id)} disabled={disabled} onToggle={toggle} testIdPrefix={testIdPrefix} />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {filteredStores.map(s => (
              <StorePill key={s.store_id} store={s} selected={value.includes(s.store_id)} disabled={disabled} onToggle={toggle} testIdPrefix={testIdPrefix} />
            ))}
          </div>
        )}
      </div>

      {helperText && (
        <p className="text-[11px] text-slate-500">{helperText}</p>
      )}
    </div>
  );
}
